import { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { LeaderboardEntry, LeaderboardScope, LeaderboardPeriod } from '@proref/types';
import { useAuth } from '../contexts/auth-context';
import { useLeaderboardQuery } from '../hooks/use-leaderboard';
import { placeholderDim } from '../lib/query-ui';
import { PageHeader } from '../components/shared/PageHeader';
import { Skeleton } from '../components/shared/Skeleton';
import { Avatar } from '../components/shared/Avatar';
import { Icon } from '../components/shared/Icons';
import { AnimatedNumber } from '../components/shared/AnimatedNumber';

// Small flame glyph for the streak column — the icon set has no flame, so it's inlined here (used
// only on this page). Inherits `currentColor` so the caller sets the tint.
function Flame({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12 2c.4 3-1.6 4.2-2.8 5.6C8 9 7 10.4 7 12.4A5 5 0 0 0 17 12.4c0-1.7-.8-3-1.7-4-.3 1-1 1.6-1.8 1.6 1-2.2.2-5.6-1.5-8z" />
    </svg>
  );
}

// XP renders with thousands separators to match the design (e.g. 8,420). Fixed en-US so the
// grouping is a comma regardless of the viewer's locale.
const fmtXp = (xp: number) => xp.toLocaleString('en-US');

// XP / accuracy figures that ease to their value — on first paint and whenever the scope refetches.
// Accuracy stays a plain em dash when there's nothing to count (no mastery yet), not an animated 0%.
function AnimatedXp({ xp }: { xp: number }) {
  return <AnimatedNumber value={xp} format={(n) => fmtXp(Math.round(n))} />;
}
function AnimatedAcc({ accuracy }: { accuracy: number | null }) {
  if (accuracy == null) return <>—</>;
  return <AnimatedNumber value={Math.round(accuracy * 100)} format={(n) => `${Math.round(n)}%`} />;
}

// Podium medal styling per rank — gold / silver / bronze. v1 bakes a single light palette, so these
// fixed metallic hexes are safe (no dark variant to reconcile). `order` reshuffles the three cards on
// sm+ so #1 sits in the MIDDLE (2 · 1 · 3) while DOM order stays 1-2-3 for the stacked mobile view.
// A function (not a module constant) since the medal NAME is translated — takes `t` as a parameter
// like every other plain (non-component) helper in this file.
function medals(t: TFunction): Record<number, { name: string; hex: string; order: string }> {
  return {
    1: { name: t('leaderboard.champion'), hex: '#d4af37', order: 'sm:order-2' }, // gold
    2: { name: t('leaderboard.second'), hex: '#9aa3ad', order: 'sm:order-1' }, // silver
    3: { name: t('leaderboard.third'), hex: '#c07b46', order: 'sm:order-3' }, // bronze
  };
}

// The line under a referee's name: their federation country + current streak (e.g. "HRV · streak 124d").
function subline(t: TFunction, entry: LeaderboardEntry): string {
  const streak = t('leaderboard.streakDays', { days: entry.current_streak });
  return entry.country ? `${entry.country} · ${streak}` : streak;
}

// A slim accuracy meter — navy fill over a faint track. Empty (null) reads as a 0-width bar so the
// column still aligns for referees who haven't answered anything yet.
function AccuracyBar({ accuracy }: { accuracy: number | null }) {
  const pct = accuracy != null ? Math.round(accuracy * 100) : 0;
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-line-2">
      <div className="h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
    </div>
  );
}

// The standings board. §9 backs this with a Redis ZSET eventually; today it's computed from the DB
// — the all-time board from users.xp/user_mastery, the week/month/season windows from xp_ledger +
// attempts via leaderboard_window (see leaderboard.service.ts on the API). Rank deltas and badges
// are still pending, so the Badges rail card is an honest placeholder.
export function LeaderboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [scope, setScope] = useState<LeaderboardScope>('federation');
  const [period, setPeriod] = useState<LeaderboardPeriod>('all');
  const { data, isLoading, isError, refetch, isPlaceholderData } = useLeaderboardQuery(scope, period);
  const board = data ?? { top: [], self: null, total: 0, period, season: null };
  const entries = board.top;
  const podium = entries.slice(0, 3);

  return (
    <>
      <PageHeader title={t('leaderboard.title')} sub={t('leaderboard.subtitle')} />

      {isLoading ? (
        <LoadingState />
      ) : isError && !data ? (
        <div className="rounded-lg border border-dashed border-danger bg-danger-tint px-6 py-12 text-center">
          <p className="text-sm text-danger">{t('leaderboard.couldNotLoad')}</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-2 text-xs font-medium text-ink underline underline-offset-2 hover:text-ink-2"
          >
            {t('common.tryAgain')}
          </button>
        </div>
      ) : (
        // Toggling Federation ↔ Global keeps the other board on screen while this one loads; dim it
        // so a rank is never read as this scope's answer before it is.
        <div className={'space-y-6 ' + placeholderDim(isPlaceholderData)} aria-busy={isPlaceholderData || undefined}>
          {/* Podium — top three of the current scope. #1 is centered + raised into a podium; cards align
              to the bottom so the taller champion card steps above 2nd/3rd. Extra top padding clears the
              champion's medallion. Hidden until at least one referee ranks. */}
          {podium.length > 0 && (
            <div className="grid gap-4 pt-6 sm:grid-cols-3 sm:items-end">
              {podium.map((e) => (
                <PodiumCard key={e.user_id} entry={e} isSelf={e.user_id === user?.id} />
              ))}
            </div>
          )}

          {/* Standings table + the Period/Badges side rail. */}
          <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
            <StandingsCard
              entries={entries}
              self={board.self}
              total={board.total}
              scope={scope}
              onScope={setScope}
              selfId={user?.id}
            />
            <div className="space-y-4">
              <PeriodCard period={period} onPeriod={setPeriod} season={board.season} />
              <BadgesCard />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Podium ───────────────────────────────────────────────────────────────────
function PodiumCard({ entry, isSelf }: { entry: LeaderboardEntry; isSelf: boolean }) {
  const { t } = useTranslation();
  const MEDALS = medals(t);
  const medal = MEDALS[entry.rank] ?? MEDALS[3]!;
  const champion = entry.rank === 1;
  // The medal hex drives the 2px frame + a soft same-colour glow; the champion card is taller and
  // lifted so the three cards read as a podium (2nd · 1st · 3rd, 1st raised).
  const frame: CSSProperties = {
    borderColor: medal.hex,
    boxShadow: `0 0 0 1px ${medal.hex}33, 0 10px 30px -12px ${medal.hex}88`,
  };
  return (
    <div
      style={frame}
      className={[
        'relative flex flex-col items-center rounded-lg border-2 bg-surface text-center',
        medal.order,
        champion ? 'px-5 pb-5 pt-9 sm:min-h-[15rem] sm:-translate-y-2' : 'px-4 pb-4 pt-8 sm:min-h-[12.5rem]',
      ].join(' ')}
    >
      {/* Medallion crowning the card — a gold trophy for the champion, a metal rank disc for 2nd/3rd. */}
      <div className="absolute -top-5 left-1/2 -translate-x-1/2">
        {champion ? (
          <div
            className="grid h-11 w-11 place-items-center rounded-full text-white shadow-md ring-4 ring-surface"
            style={{ backgroundColor: medal.hex }}
          >
            <Icon.trophy className="h-6 w-6" />
          </div>
        ) : (
          <div
            className="grid h-8 w-8 place-items-center rounded-full text-sm font-bold text-white shadow ring-4 ring-surface"
            style={{ backgroundColor: medal.hex }}
          >
            {entry.rank}
          </div>
        )}
      </div>

      <Avatar name={entry.full_name} size="lg" />

      <div className="mt-2 text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ color: medal.hex }}>
        {medal.name}
      </div>
      <div className="mt-0.5 flex items-center justify-center gap-1.5">
        <span className={'truncate font-semibold text-ink ' + (champion ? 'text-lg' : 'text-[15px]')}>
          {entry.full_name}
        </span>
        {isSelf && (
          <span className="rounded-full bg-accent px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none text-accent-ink">
            {t('leaderboard.you')}
          </span>
        )}
      </div>
      <div className="mt-0.5 truncate text-xs text-muted">{subline(t, entry)}</div>

      <div className="my-3 h-px w-full bg-line-2" />

      {/* Accuracy + XP, side by side; the champion's figures are a touch larger. */}
      <div className="grid w-full grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted">{t('leaderboard.accuracy')}</div>
          <div className={'mt-0.5 font-semibold tabular-nums text-ink ' + (champion ? 'text-3xl' : 'text-2xl')}>
            <AnimatedAcc accuracy={entry.accuracy} />
          </div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted">{t('leaderboard.xp')}</div>
          <div className={'mt-0.5 font-semibold tabular-nums text-ink ' + (champion ? 'text-3xl' : 'text-2xl')}>
            <AnimatedXp xp={entry.xp} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Standings table ──────────────────────────────────────────────────────────
// One grid template shared by the header + every row so the columns line up. Wrapped in an
// overflow-x-auto container with a min-width so it scrolls (never breaks the page) on narrow screens.
const GRID = 'grid grid-cols-[2rem_minmax(120px,1fr)_5.5rem_minmax(110px,12rem)_5rem] items-center gap-3';

function StandingsCard({
  entries,
  self,
  total,
  scope,
  onScope,
  selfId,
}: {
  entries: LeaderboardEntry[];
  self: LeaderboardEntry | null;
  total: number;
  scope: LeaderboardScope;
  onScope: (s: LeaderboardScope) => void;
  selfId?: string;
}) {
  const { t } = useTranslation();
  const SCOPES: { value: LeaderboardScope; label: string }[] = [
    { value: 'federation', label: t('leaderboard.federation') },
    { value: 'global', label: t('leaderboard.global') },
  ];
  // The top three already headline the podium cards above, so the table lists from #4 down — no need
  // to repeat the medallists row-for-row. `entries` still drives the honest "Top N of total" count.
  const listed = entries.slice(3);
  // Only the top slice is listed; note how many rank in total so "Top 10" reads honestly.
  const sub =
    total > entries.length
      ? t('leaderboard.topOfTotal', { shown: entries.length, total })
      : t('leaderboard.rankedByXp');
  return (
    <section className="overflow-hidden rounded-lg border border-line-2 bg-surface shadow-1">
      {/* Card header: title + the scope toggle (Federation / Global) that replaces the mock's cohort tabs. */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line-2 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-ink">{t('leaderboard.standings')}</h2>
          <p className="text-xs text-muted">{sub}</p>
        </div>
        <div className="inline-flex rounded-btn border border-line-2 bg-bg-2 p-0.5">
          {SCOPES.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => onScope(s.value)}
              className={
                'rounded-[6px] px-3 py-1 text-xs font-medium transition-colors ' +
                (scope === s.value ? 'bg-accent text-accent-ink' : 'text-muted hover:text-ink')
              }
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="grid place-items-center px-6 py-16 text-center">
          <Icon.trophy className="h-7 w-7 text-muted" />
          <p className="mt-3 text-sm font-medium text-ink">{t('leaderboard.noStandingsTitle')}</p>
          <p className="mt-1 text-xs text-muted">
            {scope === 'federation'
              ? t('leaderboard.noStandingsFederation')
              : t('leaderboard.noStandingsGlobal')}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[560px]">
            {/* Column header */}
            <div className={`${GRID} border-b border-line-2 bg-bg-2 px-4 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted`}>
              <span className="text-center">#</span>
              <span>{t('leaderboard.referee')}</span>
              <span>{t('leaderboard.streak')}</span>
              <span>{t('leaderboard.accuracy')}</span>
              <span className="text-right">{t('leaderboard.xp')}</span>
            </div>
            {listed.length > 0 ? (
              <ul>
                {listed.map((e) => (
                  <StandingsRow key={e.user_id} entry={e} isSelf={e.user_id === selfId} />
                ))}
              </ul>
            ) : (
              // 1–3 referees: everyone already sits on the podium, so the table below it is empty by
              // design — say so rather than showing a bare header or a false "no standings" state.
              <p className="border-b border-line-2 px-4 py-6 text-center text-xs text-muted">
                {t('leaderboard.everyoneOnPodium')}
              </p>
            )}

            {/* The caller's own standing, pinned below the top slice when they didn't make the cut —
                so a referee ranked #24 still sees exactly where they sit without scrolling a full board. */}
            {self && (
              <div className="border-t-2 border-line-3 bg-bg-2">
                <div className="flex items-center justify-between px-4 pt-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">
                    {t('leaderboard.yourPosition')}
                  </span>
                  <span className="text-[11px] font-medium tabular-nums text-ink-2">
                    {t('leaderboard.positionOfTotal', { rank: self.rank, total })}
                  </span>
                </div>
                <ul>
                  <StandingsRow entry={self} isSelf />
                </ul>
              </div>
            )}

            {/* Total-count footer — makes the board's size explicit at a glance ("this is the whole
                cohort"), so the top-N slice doesn't read as if it's everyone. */}
            {total > 0 && (
              <div className="border-t border-line-2 bg-bg-2 px-4 py-2 text-center text-[11px] font-medium tabular-nums text-muted">
                {t('leaderboard.totalReferees', { count: total })}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function StandingsRow({ entry, isSelf }: { entry: LeaderboardEntry; isSelf: boolean }) {
  const { t } = useTranslation();
  // Top-three ranks read in accent; the rest muted.
  const rankClass = entry.rank <= 3 ? 'text-accent font-bold' : 'text-muted font-medium';
  // The caller's own row gets an inset accent ring (+ a faint tint) so it's findable at a glance — the
  // "You" pill alone was easy to miss when scanning. `ring-inset` keeps it from nudging the grid.
  const selfClass = isSelf ? 'bg-accent-tint ring-1 ring-inset ring-accent' : '';
  return (
    <li className={`${GRID} border-b border-line-2 px-4 py-2.5 last:border-0 ${selfClass}`}>
      <span className={`text-center text-sm tabular-nums ${rankClass}`}>{entry.rank}</span>

      <div className="flex min-w-0 items-center gap-2.5">
        <Avatar name={entry.full_name} size="md" />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium text-ink">{entry.full_name}</span>
            {isSelf && (
              <span className="rounded-full bg-accent px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none text-accent-ink">
                {t('leaderboard.you')}
              </span>
            )}
          </div>
          {entry.country && <div className="truncate text-[11px] text-muted">{entry.country}</div>}
        </div>
      </div>

      <span className="flex items-center gap-1 text-sm tabular-nums text-ink-2">
        <Flame className="h-3.5 w-3.5 text-accent-2" />
        <AnimatedNumber value={entry.current_streak} />d
      </span>

      <div className="flex items-center gap-2">
        <span className="w-8 text-sm tabular-nums text-ink-2">
          <AnimatedAcc accuracy={entry.accuracy} />
        </span>
        <AccuracyBar accuracy={entry.accuracy} />
      </div>

      <span className="text-right text-sm font-semibold tabular-nums text-ink">
        <AnimatedXp xp={entry.xp} />
      </span>
    </li>
  );
}

// ── Side rail (honest M9 placeholders) ───────────────────────────────────────
// Real periods, computed server-side: 'all' reads the running users.xp/user_mastery totals;
// month/season read xp_ledger + attempts windowed to federation-local calendar boundaries
// (leaderboard.service.ts on the API). A single row — three labels fit this rail card's width
// without crowding.
function PeriodCard({
  period,
  onPeriod,
  season,
}: {
  period: LeaderboardPeriod;
  onPeriod: (p: LeaderboardPeriod) => void;
  season: { name: string; starts_on: string; ends_on: string } | null;
}) {
  const { t } = useTranslation();
  const PERIODS: { value: LeaderboardPeriod; label: string }[] = [
    { value: 'month', label: t('leaderboard.thisMonth') },
    { value: 'season', label: t('leaderboard.season') },
    { value: 'all', label: t('leaderboard.allTime') },
  ];
  // period='season' with no configured season silently falls back to the all-time board
  // server-side (season: null in the response) — say so here rather than showing a board that
  // looks like a season window but isn't one.
  const caption =
    period === 'season'
      ? season
        ? t('leaderboard.seasonCaption', { name: season.name })
        : t('leaderboard.seasonUnconfiguredCaption')
      : t('leaderboard.periodCaption');
  return (
    <section className="rounded-lg border border-line-2 bg-surface p-4 shadow-1">
      <h2 className="text-sm font-semibold text-ink">{t('leaderboard.period')}</h2>
      <p className="mt-0.5 text-xs text-muted">{caption}</p>
      <div className="mt-3 flex w-full rounded-btn border border-line-2 bg-bg-2 p-0.5">
        {PERIODS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => onPeriod(p.value)}
            className={
              'flex-1 rounded-[6px] px-2 py-1.5 text-center text-[11px] font-medium transition-colors ' +
              (period === p.value ? 'bg-surface text-ink shadow-1' : 'text-muted hover:text-ink')
            }
          >
            {p.label}
          </button>
        ))}
      </div>
    </section>
  );
}

// Badges (badges/user_badges tables) are deferred to M9; there's no data to render yet, so this is
// a labelled placeholder rather than invented badges.
function BadgesCard() {
  const { t } = useTranslation();
  return (
    <section className="rounded-lg border border-line-2 bg-surface p-4 shadow-1">
      <h2 className="text-sm font-semibold text-ink">{t('leaderboard.yourBadges')}</h2>
      <div className="mt-4 grid place-items-center rounded border border-dashed border-line-2 bg-bg-2 px-4 py-8 text-center">
        <Icon.spark className="h-6 w-6 text-muted" />
        <p className="mt-2 text-xs font-medium text-ink">{t('leaderboard.badgesArrive')}</p>
        <p className="mt-1 text-[11px] text-muted">{t('leaderboard.badgesCaption')}</p>
      </div>
    </section>
  );
}

// ── Loading ──────────────────────────────────────────────────────────────────
function LoadingState() {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-32 w-full rounded-lg" />
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <Skeleton className="h-96 w-full rounded-lg" />
        <div className="space-y-4">
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-40 w-full rounded-lg" />
        </div>
      </div>
    </div>
  );
}
